import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getMentalState, MentalStateResponse } from '../services/api';
import { AudioTechniquesBlock, AIChatBlock, CourseBlock } from '../components/blocks';

const QuickHelp: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useParams<{ state: string }>();
  const [data, setData] = useState<MentalStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const stateCode = state || 'anxiety';
        const response = await getMentalState(stateCode);
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [state]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error">
          {error || 'Состояние не найдено'}
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Title Section */}
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        {data.title}
      </Typography>

      {/* Quick Actions Section */}
      <AudioTechniquesBlock 
        techniques={data.quickActions}
        onStartTechnique={(technique) => {
          // Handle starting a technique
          console.log('Starting technique:', technique);
        }}
      />

      {/* Description Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            О {data.title.toLowerCase()}
          </Typography>
          <Typography paragraph>
            {data.description}
          </Typography>
        </CardContent>
      </Card>

      {/* Symptoms Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon color="primary" />
            Возможные симптомы
          </Typography>
          <List>
            {data.symptoms.map((symptom, index) => (
              <ListItem key={index}>
                <ListItemText primary={`• ${symptom}`} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* AI Chat Section */}
      <AIChatBlock 
        onStartChat={() => navigate('/chat')}
      />

      {/* Course Section */}
      <CourseBlock 
        title={`Курс по работе с ${data.title.toLowerCase()}`}
        description={`Пройдите наш специальный курс, чтобы научиться эффективно справляться с ${data.title.toLowerCase()} в повседневной жизни.`}
        resources={data.resources}
        onStartCourse={() => navigate('/courses')}
      />
    </Container>
  );
};

export default QuickHelp; 